import { useState, useEffect, useCallback } from 'react';
import { Flex, ResponsiveLogmarSelection, NavButtons } from '../../../2022-component-library';
import { ResponsiveKaysOptotypesSetup, ResponsiveKaysOptotypesTestOptician } from '.';
import { ResponsiveAcuityResults } from '..';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useResultData } from '../../../helpers/useResultData';

const ResponsiveKaysOptotypesContainer = ({ setActiveTab, setTestStep }) => {
  const { testData, updateTestData } = useTestContext();
  const { clearCurrentEyeResults } = useResultData();
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const [logmar, setLogmar] = useState(null);
  const [canProgress, setCanProgress] = useState(false);
  const [step, setStep] = useState('eyeSetup');
  const [nextStep, setNextStep] = useState('');
  const [backStep, setBackStep] = useState('');

  useEffect(() => {
    let selectedEye;
    switch (true) {
      case step === 'eyeSetup':
        setBackStep('selectTest');
        setNextStep(testData.testBothEyes ? 'bothEyesSetup' : testData.testRightEye ? 'rightEyeSetup' : 'leftEyeSetup');
        break;
      case step.endsWith('Setup'):
        setCanProgress(true);
        selectedEye = step.split('Setup')[0];
        if (selectedEye === 'leftEye' && testData.testRightEye) {
          updateTestData({ testInProgress: true });
        } else updateTestData({ testInProgress: false });
        setNextStep(`${selectedEye}Test`);
        setBackStep(selectedEye === 'leftEye' && testData.testRightEye ? 'rightEyeSetup' : 'eyeSetup');
        break;
      case step.endsWith('Test'):
        selectedEye = step.split('Test')[0];
        clearCurrentEyeResults(selectedEye);
        updateTestData({ testInProgress: true });
        setNextStep(selectedEye === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results');
        setBackStep(`${selectedEye}Setup`);
        break;
      default:
        break;
    }
  }, [step, testData.testRightEye, testData.testLeftEye, testData.testBothEyes, updateTestData, clearCurrentEyeResults]);

  const updateStep = useCallback((selectedStep) => {
    setCanProgress(false);
    if (selectedStep === 'selectTest') {
      setTestStep('selectTest');
      postMessage({ type: 'holding' });
    } else {
      setBackStep(step);
      if (selectedStep.endsWith('Setup')) {
        postMessage({ type: 'holding' });
      }
      if (selectedStep === 'results') postMessage({ type: 'endOfTest' });
      setStep(selectedStep);
    }
  }, []);

  useEffect(() => {
    if (receivedMessage?.type === 'beginTestAgain') updateStep('eyeSetup');
  }, [receivedMessage, updateStep]);

  const renderSwitch = () => {
    switch (step) {
      case 'eyeSetup':
        return <ResponsiveKaysOptotypesSetup setCanProgress={setCanProgress} />;
      case 'bothEyesSetup':
      case 'rightEyeSetup':
      case 'leftEyeSetup':
        return <ResponsiveLogmarSelection eyeTested={step.split('Setup')[0]} setLogmar={setLogmar} noCharacters />;
      case 'rightEyeTest':
      case 'leftEyeTest':
      case 'bothEyesTest':
        return <ResponsiveKaysOptotypesTestOptician eyeTested={step.split('Test')[0]} goToTestResults={() => updateStep(nextStep)} />;
      case 'results':
        return <ResponsiveAcuityResults setActiveTab={setActiveTab} />;
      default:
        return;
    }
  };

  return (
    <Flex column centered>
      {renderSwitch()}
      {step !== 'results' && (
        <NavButtons marginTop='0.5rem' updateStep={(direction) => updateStep(direction === 'back' ? backStep : nextStep)} canProgress={canProgress} />
      )}
    </Flex>
  );
};

export default ResponsiveKaysOptotypesContainer;
